import { config } from "../../shared/config/environment";
import { fetchWithRefresh, type RefreshTokenFn } from "../../shared/api/fetchWithRefresh";
import type { Channel } from "../../shared/host";
import { getDisabledSkillKeysList } from "../../shared/userSkillPrefs";
import type { EmailContext, EmailDraft, EmailSearchResult } from "../../integrations/email/types";
import type { WordDocumentContext } from "../../document/types";
import { DECK_CONTEXT_CHAR_CAP } from "../../integrations/powerpoint/const";
import type { ChatAttachment, ChatMessage, SSEEvent } from "../types";
import type { NBASSEEvent } from "../../integrations/engage/types";

// /api/v1/{config,nba,nba/execute,nba/.../status} and the /api/v2/chat
// endpoints all live on the Python FastAPI service (apps/backend/ai/API),
// so this module routes through `aiApiBaseUrl`. Per-connection external
// API URLs (e.g. the Engage-platform's own hub host) come from the
// connection registry, not from here.

// Chat moved to the v2 contract in AIVA-5929: server-side threads (no
// client-sent history, `thread_id` instead of `session_id`) and a
// discriminated resume payload (kind = tool_result | approval). Everything
// non-chat stays on v1.
const CHAT_API_PREFIX = "/api/v2/chat";

/* ── App config from backend ──────────────────── */

export interface AuthConfigResponse {
  client_id: string;
  authority: string;
  scopes: string[];
}

interface AppConfigResponse {
  auth: AuthConfigResponse;
}

/**
 * Returns common headers for backend API calls.
 */
function buildApiHeaders(contentType?: string): Record<string, string> {
  const headers: Record<string, string> = {};
  if (contentType) {
    headers["Content-Type"] = contentType;
  }
  return headers;
}

/**
 * Fetches application config (MSAL auth values) from the backend.
 */
export async function fetchConfig(): Promise<AppConfigResponse> {
  // Routed through fetchWithRefresh (no refresh callback — boot-time call) so it
  // emits the same perf.api.call / error.api.* telemetry as every other backend
  // call. With no refresh fn the behaviour is identical to a bare fetch.
  const response = await fetchWithRefresh(`${config.aiApiBaseUrl}/api/v1/config`, {
    // /api/v1/config returns MSAL bootstrap values served by the ai-api.
    headers: buildApiHeaders(),
  });
  if (!response.ok) {
    throw new Error(`Config fetch failed: ${response.status}`);
  }
  return response.json() as Promise<AppConfigResponse>;
}

/* ── SSE helpers ─────────────────────────────── */

/**
 * Attempts to parse a single SSE chunk and dispatch it.
 * Returns `true` when the terminal event is received.
 */
function parseSSELine<T>(
  line: string,
  onEvent: (event: T) => void,
  doneEvent: string,
): boolean {
  const trimmed = line.trim();
  if (!trimmed.startsWith("data: ")) return false;

  try {
    const parsed = JSON.parse(trimmed.slice(6)) as T & { event: string };
    onEvent(parsed);
    return parsed.event === doneEvent;
  } catch {
    // Skip malformed SSE lines
    return false;
  }
}

/* ── Shared SSE reader ────────────────────────── */

async function readSSEStream<T>(
  response: Response,
  onEvent: (event: T) => void,
  doneEvent: string,
): Promise<void> {
  const reader = response.body?.getReader();
  if (!reader) {
    throw new Error("No response body");
  }

  const decoder = new TextDecoder();
  let buffer = "";

  while (true) {
    const { done, value } = await reader.read();
    if (done) break;

    buffer += decoder.decode(value, { stream: true });
    const lines = buffer.split("\n\n");
    buffer = lines.pop() ?? "";

    for (const line of lines) {
      if (parseSSELine(line, onEvent, doneEvent)) return;
    }
  }
}

/* ── Chat streaming ───────────────────────────── */

/**
 * Carries the HTTP status of a failed chat/resume call so the caller can
 * map 401 to a session-expired flow, 429/5xx to a "busy" message, etc.,
 * instead of falling into a single generic "Something went wrong" path.
 */
export class ChatHttpError extends Error {
  readonly status: number;
  readonly endpoint: "chat" | "resume";
  constructor(status: number, endpoint: "chat" | "resume") {
    super(`Chat ${endpoint === "resume" ? "resume " : ""}request failed: ${status}`);
    this.name = "ChatHttpError";
    this.status = status;
    this.endpoint = endpoint;
  }
}

/**
 * Client-owned context re-sent verbatim on every turn *and* every resume
 * (AIVA-5925). This is the single source of truth for the set of context
 * fields the add-in carries: declare a new field here and it flows through the
 * initial turn (`streamChat`) and every resume (`resumeChat`) automatically,
 * because both `ChatStreamContext` and `ResumeChatOptions` extend it and both
 * serialize via `serializeTurnContext`. Mirrors the backend `TurnContextV2`.
 */
/**
 * The open document as it rides a turn. Word sends its document or selection;
 * PowerPoint sends the current slide in the same shape (AIVA-6809, SC-01), plus
 * where that slide sits in the deck. One envelope, so both get the backend's
 * untrusted-content screening; the backend words the prompt block per host.
 */
export type DocumentTurnContext = WordDocumentContext & {
  /** PowerPoint only: the selected text or shapes; `text` is then the whole slide. */
  selectedText?: string;
  /** PowerPoint only: the current slide's 1-based position. */
  slideNumber?: number;
  slideCount?: number;
  /** PowerPoint only: "deck" when `text` is the whole deck (DI-01 summary), not the current slide. */
  scope?: "slide" | "deck";
};

export interface TurnContext {
  emailContext?: EmailContext | null;
  documentContext?: DocumentTurnContext | null;
  latestDraft?: EmailDraft | null;
  latestSearchResults?: EmailSearchResult[];
  /** User-uploaded files. Client-owned context re-sent on the initial turn AND
   *  every resume: the attachment write tools are bound only when attachments
   *  are present, so a paused (HITL-gated) attachment draft must re-send them
   *  on approval or the approved tool is no longer bound (AIVA-6391). */
  attachments?: ChatAttachment[];
  /** Which Outlook surface the user is on — `"read"` or `"compose"`. Drives which
   *  action pills render, and lets a reply-type skill refuse cleanly when it is
   *  invoked from a compose window with no email to reply to.
   *
   *  Sent as its own field rather than inside `emailContext` because
   *  `buildEmailContext` returns null on an empty subject, which would make a
   *  brand-new compose window indistinguishable from "no email open".
   *  Omitted when null (no item open, or a non-Outlook host). */
  surface?: "read" | "compose" | null;
  /** Decisions the user has already made, re-sent until the action they belong to
   *  resolves (AIVA-7482).
   *
   *  Both of these are destroyed by asking a question. The backend rebuilds
   *  `verified_slots` empty on every request and seeds it from *that turn's*
   *  message, so the external-recipient confirmation — which costs a turn —
   *  wiped the slot the user had just clicked, and the booking was refused for a
   *  time they demonstrably chose. Carrying them here is the client-owned-context
   *  pattern of ADR-chat-v2-hitl-resume-context; deliberately NOT server state. */
  confirmations?: TurnConfirmations | null;
  /** The signed-in user's display name, from `Office.context.mailbox.userProfile`.
   *
   *  Sent because the model had no way to know it. The prompt carried the user's
   *  email ADDRESS and nothing else, so when a draft needed a sign-off the model
   *  filled the gap from whatever name was nearest — signing as AiVA once and as
   *  the open email's sender the next time. Deriving a name from the address is
   *  banned by spec gap G-8 ("n.morgan@… produces 'Hi Morgan'"), so it has to be
   *  supplied.
   *
   *  Office.js already has it, which is why this is a field and not a Graph call. */
  userDisplayName?: string | null;
}

/** The slot the user picked, carried until the booking resolves. */
export interface ConfirmedSlot {
  /** Naive local start, `YYYY-MM-DDTHH:MM:SS` — the same shape the book-slot pill
   *  puts in its `time_quote`, so the backend rebuilds an identical slot record. */
  startIso: string;
  durationMinutes: number;
}

/** User answers that must outlive the turn they were given on. */
export interface TurnConfirmations {
  bookingSlot?: ConfirmedSlot | null;
  /** Addresses the user has explicitly approved for sending outside the
   *  organisation. Per address, never per domain — the backend refuses to let an
   *  approval of one client contact cover their colleague. */
  externalRecipients?: string[];
}

/**
 * How much Word document/selection text rides a chat turn. Everything past
 * this is silently dropped by the slice below, so anything the user should be
 * told about that cap (the over-cap selection warning in useWordDocument)
 * must reference THIS constant — one number, one source (AIVA-7002/W52
 * family). Applies to the whole-document case too (AIVA-7738): the entire
 * document must never ride a turn uncapped.
 */
export const DOCUMENT_CONTEXT_CHAR_CAP = 6000;

/**
 * The ``document_context`` wire shape. Word's fields always; PowerPoint's
 * selection and slide position only when present, so a Word payload is
 * byte-for-byte what it was before PowerPoint shared this envelope.
 */
function serializeDocumentContext(dc: DocumentTurnContext): Record<string, unknown> {
  // A whole deck (DI-01) has its own, larger cap.
  const cap = dc.scope === "deck" ? DECK_CONTEXT_CHAR_CAP : DOCUMENT_CONTEXT_CHAR_CAP;
  const out: Record<string, unknown> = {
    name: dc.name,
    text: dc.text.slice(0, cap),
    word_count: dc.wordCount,
    char_count: dc.charCount,
    has_selection: dc.hasSelection,
  };
  if (dc.selectedText) out.selected_text = dc.selectedText.slice(0, DOCUMENT_CONTEXT_CHAR_CAP);
  if (typeof dc.slideNumber === "number") out.slide_number = dc.slideNumber;
  if (typeof dc.slideCount === "number") out.slide_count = dc.slideCount;
  if (dc.scope === "deck") out.scope = "deck";
  return out;
}

/**
 * Serializes {@link TurnContext} into its `snake_case` request-body fragment.
 * The ONE place the wire mapping lives — shared by `streamChat` and
 * `resumeChat` so the two can never drift (they previously did: the resume
 * builder silently dropped `has_selection`).
 */
export function serializeTurnContext(c: TurnContext): Record<string, unknown> {
  const body: Record<string, unknown> = {};
  if (c.emailContext) {
    body.email_context = c.emailContext;
  }
  if (c.documentContext && c.documentContext.wordCount > 0) {
    body.document_context = serializeDocumentContext(c.documentContext);
  }
  if (c.latestDraft) {
    body.latest_draft = c.latestDraft;
  }
  if (c.latestSearchResults && c.latestSearchResults.length > 0) {
    body.latest_search_results = c.latestSearchResults;
  }
  if (c.attachments && c.attachments.length > 0) {
    body.attachments = c.attachments;
  }
  if (c.surface) {
    body.surface = c.surface;
  }
  if (c.userDisplayName) {
    body.user_display_name = c.userDisplayName;
  }
  const confirmations = serializeConfirmations(c.confirmations);
  if (confirmations) {
    body.confirmations = confirmations;
  }
  return body;
}

/**
 * Wire form of {@link TurnConfirmations}, or `undefined` when there is nothing
 * to carry.
 *
 * Omitting the key entirely on an empty object matters: an older backend ignores
 * an unknown field, but sending `{}` on every turn would put a meaningless key in
 * every request body and make the "is a booking mid-confirmation?" question
 * ambiguous to read in a trace.
 */
function serializeConfirmations(c: TurnConfirmations | null | undefined): Record<string, unknown> | undefined {
  if (!c) return undefined;
  const out: Record<string, unknown> = {};
  if (c.bookingSlot) {
    out.booking_slot = {
      start_iso: c.bookingSlot.startIso,
      duration_minutes: c.bookingSlot.durationMinutes,
    };
  }
  if (c.externalRecipients && c.externalRecipients.length > 0) {
    out.external_recipients = c.externalRecipients;
  }
  return Object.keys(out).length > 0 ? out : undefined;
}

export interface ChatStreamContext extends TurnContext {
  /** When set, bypasses the backend skill router and uses this Cosmos skill key.
   *  Wired by the slash-command menu (e.g. ``/reply`` → ``"email_draft"``).
   *  ``undefined`` / ``null`` ⇒ the backend classifies via the LLM router as usual. */
  forcedSkillKey?: string | null;
  /** AIVA-8157: this turn replays one step of a multipart plan rather than
   *  being a user-typed slash. Both set ``forcedSkillKey``; only a replay must
   *  skip the planner, or each step would be re-planned into another plan. */
  fromPlanStep?: boolean;
  /** What the pane rendered as the user bubble when it differs from the sent
   *  message (pill clicks and slash commands send an engineered prompt while
   *  showing something readable). Persisted server-side as the transcript's
   *  user content so a relaunch rehydrates the friendly bubble instead of the
   *  raw prompt — client-side pairing (AIVA-6899) dies with the session cache. */
  displayContent?: string | null;
  /** True when the pane rendered no user bubble for this turn (one-tap pill
   *  shortcuts): the transcript skips the user message entirely. */
  hiddenTurn?: boolean;
  /** Disclaimer text to append to every AI-generated email draft. Sourced from
   *  tenant OutlookIntegrationSettings; forwarded so the backend can embed it in
   *  the Graph-saved Drafts-folder copy. */
  disclaimerText?: string | null;
  /** The persona display name as shown in the Studio UI header. Forwarded so the
   *  backend uses the same name in out-of-scope error messages rather than the
   *  SQL Personas.Name which may differ. */
  personaDisplayName?: string | null;
  /** Per-context thread scoping (Word document URL). Mirrors the localStorage
   *  bucket keying so server threads isolate the same way (chat v2). */
  contextId?: string | null;
  /** False when Microsoft 365 is unreachable. The backend refuses skills whose
   *  inputRequirements declare requiresMicrosoft365 rather than running an agent
   *  that would fail on a Graph call. Omitted when true — the backend defaults to
   *  reachable, so an older server simply ignores it. */
  m365Connected?: boolean;
  /** AIVA-8792 — PowerPoint only: whether the Deckorate module is enabled. True
   *  lets v2 hand a Deckorate request back (a `handoff` event); false tells it
   *  the module is off. Omitted for other hosts, which the backend reads as
   *  "unknown" and never hands off. */
  deckorateEnabled?: boolean;
  /** AIVA-9184 — PowerPoint only: this add-in can run a `rephrase_slide`
   *  handoff, so v2 may route "improve this slide" to the slide rewrite. When
   *  omitted, the backend keeps that skill forced-only. */
  rephraseSlideHandoff?: boolean;
  signal?: AbortSignal;
  /** Called once on a 401 to silently mint a fresh bearer and retry the request. */
  refresh?: RefreshTokenFn;
}

function buildChatHeaders(apiToken: string | null): Record<string, string> {
  const headers = buildApiHeaders("application/json");
  if (apiToken) {
    headers["Authorization"] = `Bearer ${apiToken}`;
  }
  return headers;
}

function buildChatBody(
  message: string,
  _history: ChatMessage[],
  threadId: string | null,
  host: Channel,
  context: ChatStreamContext,
): Record<string, unknown> {
  // v2: no history in the body — the server-side thread store is the source
  // of truth (the parameter is kept so call sites stay stable).
  const body: Record<string, unknown> = {
    message,
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    host,
    // Client-owned context — one serializer shared with resumeChat (AIVA-5925).
    ...serializeTurnContext(context),
  };
  // A null thread id is simply omitted — the backend treats a missing
  // thread_id as "start a new thread" and returns the minted id on the
  // terminal done/paused event.
  if (threadId) {
    body.thread_id = threadId;
  }
  if (context.contextId) {
    body.context_id = context.contextId;
  }
  if (context.forcedSkillKey) {
    body.forced_skill_key = context.forcedSkillKey;
  }
  if (context.fromPlanStep) {
    body.from_plan_step = true;
  }
  if (context.hiddenTurn) {
    body.hidden = true;
  } else if (context.displayContent && context.displayContent !== message) {
    body.display_content = context.displayContent;
  }
  // attachments are serialized by serializeTurnContext (shared client-owned
  // context) so they ride resume too — no separate handling here.
  if (context.disclaimerText) {
    body.disclaimer_text = context.disclaimerText;
  }
  if (context.personaDisplayName) {
    body.persona_display_name = context.personaDisplayName;
  }
  if (context.m365Connected === false) {
    body.m365_connected = false;
  }
  if (typeof context.deckorateEnabled === 'boolean') {
    body.deckorate_enabled = context.deckorateEnabled;
  }
  if (context.rephraseSlideHandoff) {
    body.rephrase_slide_handoff = true;
  }
  // User-level skill disable list (Settings > Skills, ST-04/ST-07): read
  // fresh from storage on every send so the router-side subtraction always
  // reflects the latest toggles. Omitted when empty — older backends ignore
  // the field either way. A preference, not a security boundary: the server
  // still enforces the persona allow-list independently.
  const disabledSkillKeys = getDisabledSkillKeysList(host);
  if (disabledSkillKeys.length > 0) {
    body.disabled_skill_keys = disabledSkillKeys;
  }
  return body;
}

export async function streamChat(
  message: string,
  history: ChatMessage[],
  threadId: string | null,
  host: Channel,
  apiToken: string | null,
  onEvent: (event: SSEEvent) => void,
  context: ChatStreamContext = {},
): Promise<void> {
  const headers = buildChatHeaders(apiToken);
  const body = buildChatBody(message, history, threadId, host, context);

  const response = await fetchWithRefresh(
    `${config.aiApiBaseUrl}${CHAT_API_PREFIX}`,
    {
      method: "POST",
      headers,
      body: JSON.stringify(body),
      signal: context.signal,
    },
    context.refresh,
  );

  if (!response.ok) {
    throw new ChatHttpError(response.status, "chat");
  }

  await readSSEStream(response, onEvent, "done");
}

/* ── Resume after a frontend tool_call ────────── */

export interface ResumeChatOptions extends TurnContext {
  signal?: AbortSignal;
  history?: ChatMessage[];
  /** v2 discriminator — "tool_result" (default; frontend Office JS tools)
   *  or "approval" (HITL approval decision). */
  kind?: "tool_result" | "approval";
  /** The human decision when kind === "approval". */
  approval?: { decision: "approve" | "reject"; message?: string };
  /** Called once on a 401 to silently mint a fresh bearer and retry the request. */
  refresh?: RefreshTokenFn;
}

export async function resumeChat(
  threadId: string,
  interruptId: string,
  result: Record<string, unknown>,
  host: Channel,
  apiToken: string | null,
  onEvent: (event: SSEEvent) => void,
  options: ResumeChatOptions = {},
): Promise<void> {
  const headers = buildChatHeaders(apiToken);
  const kind = options.kind ?? "tool_result";
  const body: Record<string, unknown> = {
    thread_id: threadId,
    interrupt_id: interruptId,
    kind,
    host,
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    // Re-send the client-owned context so the paused tool rebuilds with the
    // same message-id / draft / search context (AIVA-5925). One serializer,
    // shared with streamChat — the two can no longer drift.
    ...serializeTurnContext(options),
  };
  if (kind === "approval") {
    body.approval = options.approval;
  } else {
    body.result = result;
  }

  const response = await fetchWithRefresh(
    `${config.aiApiBaseUrl}${CHAT_API_PREFIX}/resume`,
    {
      method: "POST",
      headers,
      body: JSON.stringify(body),
      signal: options.signal,
    },
    options.refresh,
    undefined,
    // A resume continues a chat turn that already ran the access re-validation at
    // its start (the /chat call). Re-fetching /settings before every resume put
    // an N+1 chain of serial settings round-trips on the turn's critical path
    // (one per frontend-tool loop), visibly delaying the streamed response. Skip
    // the preflight here; a mid-session access change is caught on the next /chat.
    { skipAccessCheck: true },
  );

  if (!response.ok) {
    throw new ChatHttpError(response.status, "resume");
  }

  await readSSEStream(response, onEvent, "done");
}

/* ── Deckorate chat streaming (dedicated PowerPoint endpoints) ────────── */
//
// Separate endpoints from /chat and /chat/resume above — DeckorateChat routes
// to a different, PowerPoint-only skillset, gated server-side by an explicit
// is_deckorate flag (see DeckorateChat/router.py). No host field needed: the
// endpoints are PowerPoint-only by construction. is_deckorate is hardcoded
// true here — there's no second PowerPoint add-in yet to distinguish from;
// see the plan notes for how that'd be detected once one exists.

export interface DeckorateChatStreamContext {
  signal?: AbortSignal;
  /** Called once on a 401 to silently mint a fresh bearer and retry the request. */
  refresh?: RefreshTokenFn;
}

export async function streamDeckorateChat(
  message: string,
  history: ChatMessage[],
  sessionId: string | null,
  apiToken: string | null,
  onEvent: (event: SSEEvent) => void,
  context: DeckorateChatStreamContext = {},
): Promise<void> {
  const headers = buildChatHeaders(apiToken);
  const body: Record<string, unknown> = {
    message,
    history: history.map((m) => ({ role: m.role, content: m.content })),
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    is_deckorate: true,
  };
  // Null until a turn hands one back: the field is omitted rather than sent as
  // "" so the server mints the session id and returns it on the terminal
  // (done/paused) event, which onThreadIdResolved adopts.
  if (sessionId) {
    body.session_id = sessionId;
  }

  const response = await fetchWithRefresh(
    `${config.aiApiBaseUrl}/api/v1/deckorate/chat`,
    {
      method: "POST",
      headers,
      body: JSON.stringify(body),
      signal: context.signal,
    },
    context.refresh,
  );

  if (!response.ok) {
    throw new ChatHttpError(response.status, "chat");
  }

  await readSSEStream(response, onEvent, "done");
}

export interface DeckorateResumeOptions {
  signal?: AbortSignal;
  history?: ChatMessage[];
  refresh?: RefreshTokenFn;
}

export async function resumeDeckorateChat(
  sessionId: string,
  toolCallId: string,
  result: Record<string, unknown>,
  apiToken: string | null,
  onEvent: (event: SSEEvent) => void,
  options: DeckorateResumeOptions = {},
): Promise<void> {
  const headers = buildChatHeaders(apiToken);
  const body: Record<string, unknown> = {
    session_id: sessionId,
    tool_call_id: toolCallId,
    result,
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    is_deckorate: true,
  };
  if (options.history?.length) {
    body.history = options.history.map((m) => ({ role: m.role, content: m.content }));
  }

  const response = await fetchWithRefresh(
    `${config.aiApiBaseUrl}/api/v1/deckorate/chat/resume`,
    {
      method: "POST",
      headers,
      body: JSON.stringify(body),
      signal: options.signal,
    },
    options.refresh,
    undefined,
    // Mirrors resumeChat: a resume continues a turn that already ran the
    // access re-validation at its start (the /deckorate/chat call).
    { skipAccessCheck: true },
  );

  if (!response.ok) {
    throw new ChatHttpError(response.status, "resume");
  }

  await readSSEStream(response, onEvent, "done");
}

/* ── NBA streaming ────────────────────────────── */

export async function streamNBAs(
  apiToken: string,
  services: string[] | undefined,
  onEvent: (event: NBASSEEvent) => void,
  refresh?: RefreshTokenFn,
): Promise<void> {
  const headers = buildApiHeaders("application/json");
  headers["Authorization"] = `Bearer ${apiToken}`;

  const response = await fetchWithRefresh(
    `${config.aiApiBaseUrl}/api/v1/nba`,
    {
      method: "POST",
      headers,
      body: JSON.stringify(services ? { services } : {}),
    },
    refresh,
  );

  if (!response.ok) {
    throw new Error(`NBA request failed: ${response.status}`);
  }

  await readSSEStream(response, onEvent, "nba_done");
}

/* ── NBA status update ────────────────────────── */

/** The bearer token identifies whose to-do this is; the body carries no identity. */
export async function updateNbaStatus(
  nbaId: string,
  isCompleted: boolean,
  apiToken: string,
  refresh?: RefreshTokenFn,
): Promise<void> {
  const headers = buildApiHeaders("application/json");
  headers["Authorization"] = `Bearer ${apiToken}`;

  await fetchWithRefresh(
    `${config.aiApiBaseUrl}/api/v1/nba/${encodeURIComponent(nbaId)}/status`,
    {
      method: "PATCH",
      headers,
      body: JSON.stringify({ is_completed: isCompleted }),
    },
    refresh,
  );
}

/* ── Slash commands ───────────────────────────── */

export interface SlashCommandDTO {
  readonly slash: string;
  readonly label: string;
  readonly description: string;
  readonly hint: string;
  readonly prompt: string;
  readonly category: string;
  readonly autoApply: boolean;
  readonly sortOrder: number;
  readonly skillKey: string;
  readonly inputRequirements?: {
    readonly requiresWordSelection?: boolean;
    readonly requiresMicrosoft365?: boolean;
    readonly requiresSubjectOrSelection?: boolean;
    readonly requiresSlideTextSelection?: boolean;
    readonly nudgeIfMissing?: string | null;
  };
}

/** One entry in the `/?` capability listing. */
export interface CapabilityItemDTO {
  readonly slash: string;
  readonly label: string;
  readonly description: string;
  readonly requiresSelection?: boolean;
}

/** A titled group of capabilities. Titles and order are server-owned. */
export interface CapabilityGroupDTO {
  readonly key: string;
  readonly title: string;
  readonly items: readonly CapabilityItemDTO[];
}

/** Payload behind `/?`, `/help`, `/skills`, `/list`. */
export interface CapabilityListingDTO {
  readonly channel: string;
  readonly total: number;
  readonly groups: readonly CapabilityGroupDTO[];
}

/**
 * Fetch the grouped capability listing for a channel.
 *
 * Same persona-scoped skill set as the slash menu, but grouped server-side so
 * the taxonomy can change without an add-in release. Returns a listing with no
 * groups for an unauthenticated or unmapped caller — callers should fall back
 * to rendering from the local command cache rather than showing nothing.
 */
export async function fetchCapabilityListing(
  channel: string,
  apiToken: string | null,
  refresh?: RefreshTokenFn,
): Promise<CapabilityListingDTO> {
  const headers = buildApiHeaders();
  if (apiToken) {
    headers["Authorization"] = `Bearer ${apiToken}`;
  }
  const response = await fetchWithRefresh(
    `${config.aiApiBaseUrl}/api/v1/skills/capabilities/${encodeURIComponent(channel)}`,
    { headers },
    refresh,
  );
  if (!response.ok) {
    throw new Error(`Capability listing fetch failed: ${response.status}`);
  }
  return response.json() as Promise<CapabilityListingDTO>;
}

export async function fetchSlashCommands(
  channel: string,
  apiToken: string | null,
  refresh?: RefreshTokenFn,
): Promise<SlashCommandDTO[]> {
  const headers = buildApiHeaders();
  if (apiToken) {
    headers["Authorization"] = `Bearer ${apiToken}`;
  }
  const response = await fetchWithRefresh(
    `${config.aiApiBaseUrl}/api/v1/skills/slash-commands/${encodeURIComponent(channel)}`,
    { headers },
    refresh,
  );
  if (!response.ok) {
    throw new Error(`Slash commands fetch failed: ${response.status}`);
  }
  return response.json() as Promise<SlashCommandDTO[]>;
}
